/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Tooltip,
  Badge,
  Box,
} from '@mui/material';
import { CustomStack, CustomToggleSwitch } from 'modules/common/components';
import { KEYS, OTB_VALUE, REPORT_TYPE } from 'modules/dashboard/constants';
import CustomDatePicker from 'modules/dashboard/components/tab-container/inner-filter/components/custom-date-comparison';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { PRIMARY } from 'modules/dashboard/components/tab-container/trend-tabs/constants';
import CollapseIcon from 'modules/common/components/collapse-icon';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CircleIcon from '@mui/icons-material/Circle';
/**
 * trend time Vs comparison window component common for the dashboard component
 * @param {Boolean} customBooking - Custom booking selected state
 * @param {Boolean} alignmentToggle - Alignment toggle on/off state
 * @param {Function} handleToggleChange - Function to call when alignment toggle value changes
 * @param {Boolean} symmetricComparisonToggle - Symmetric comparison toggle on/off state
 * @param {Object} visual - PBI visuals for comparison window
 * @param {*} dateComparison - Selected date comparison value
 * @param {Array} dateComparisonList - Date comparison list
 * @param {Function} handleComparisonChange - Function to call when date comparison value changes
 * @param {Function} handleSymmetricComparisonChange - Function to call symmetric comparison value changes
 * @param {Boolean} weeklyViewToggle - weekly view toggle state
 * @param {Boolean} isMultiple - is Multiple state
 * @param {Date} customDate - Custom date
 * @param {Date} startDate -  Start date
 * @param {Date} endDate - End date
 * @param {Date} targetDate - Target date
 * @param {*} reportType - reportType
 * @param {Function} handleCustomDates - Function to call when custom dates value changes
 * @returns
 */
const TrendTimeVsComparisonWindow = ({
  customBooking,
  alignmentToggle,
  handleToggleChange,
  symmetricComparisonToggle,
  visual,
  visualName,
  dateComparison,
  dateComparisonList,
  handleComparisonChange,
  handleSymmetricComparisonChange,
  weeklyViewToggle,
  activeTab,
  showBreakdown = true,
  customDate = null,
  reportType = null,
  startDate = null,
  endDate = null,
  targetDate = null,
  handleCustomDates = () => {},
  isDisplay,
  isDisplayOnClickAction = () => {},
  isPrimary,
  handlePrimaryChange,
  id = '',
  hasLeap = false,
  isUnalignedMonthComparisonSpecialDate = false,
}) => (
  <CustomStack cssClassName={styles.innerFilter}>
    <FormControl>
      <Grid container justifyContent="space-between" flexDirection="row" alignItems="flex-start">
        <Grid item xs={4} paddingTop={1}>
          <FormLabel sx={{ fontSize: 13 }}>VS. Time</FormLabel>
        </Grid>

        {/* <Grid item xs={10} container flexDirection="row">
          <Grid
            container
            item
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={11}>
              <Typography fontSize={11}>{visualName}</Typography>
            </Grid>
            <Grid item xs={1}>
              <CollapseIcon isDisplay={isDisplay} onClick={isDisplayOnClickAction} tooltipText />
            </Grid>
            <Grid item xs={12}>
              {isDisplay && visual}
            </Grid>
          </Grid>
        </Grid> */}
      </Grid>
      <br />
      <CustomToggleSwitch
        label="Alignment"
        secondaryLabel="day-of-week"
        title="Align day-of-week between selected & comparison stay dates."
        onChange={
          weeklyViewToggle && activeTab === REPORT_TYPE.TREND_TIME ? undefined : handleToggleChange
        }
        checked={alignmentToggle}
        paddingBottom={3}
        className="alignment-toggle"
      />
      <CustomToggleSwitch
        label="Same Time Last Period"
        title="Use comparable lead-time and booking window between selected & comparison stay dates."
        onChange={handleSymmetricComparisonChange}
        checked={symmetricComparisonToggle}
        paddingBottom={3}
        className="sltp-toggle"
      />
      <Grid item xs={10} container flexDirection="row" paddingBottom={2}>
        <Grid container item flexDirection="row" alignItems="center" justifyContent="space-between">
          <Grid item xs={11}>
            <Typography fontSize={11}>{visualName}</Typography>
          </Grid>
          <Grid item xs={1}>
            <CollapseIcon isDisplay={isDisplay} onClick={isDisplayOnClickAction} tooltipText />
          </Grid>
          <Grid item xs={12} className='symmetric-comparison-window'>
            {isDisplay && visual}
          </Grid>
        </Grid>
      </Grid>
      {showBreakdown && (
        <FormGroup>
          <FormControlLabel
            key={0}
            value={PRIMARY}
            control={<Checkbox icon={<CircleOutlinedIcon />} checkedIcon={<CircleIcon />} />}
            label={PRIMARY}
            checked={isPrimary}
            onChange={handlePrimaryChange}
            sx={{
              mb: 1,
            }}
          />
        </FormGroup>
      )}
      <FormGroup>
        <RadioGroup value={dateComparison} onChange={handleComparisonChange}  className='comparison-date-range'>
          {dateComparisonList?.map((item) => (
            <FormControlLabel
              key={item?.id}
              value={item?.value}
              control={<Radio id={`${id}-${item?.label.toLowerCase()?.replace(' ', '-')}`} />}
              disabled={
                (item?.value === OTB_VALUE && !customBooking) ||
                activeTab === REPORT_TYPE.TREND_HOTEL ||
                (item?.value === OTB_VALUE && symmetricComparisonToggle && customBooking)
              }
              sx={{ my: -1 }}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {item?.label}
                  {((dateComparison === item?.value && hasLeap) ||
                    (dateComparison === item?.value &&
                      activeTab === REPORT_TYPE.TREND_TIME &&
                      isUnalignedMonthComparisonSpecialDate)) && (
                    <Tooltip
                      title={
                        activeTab === REPORT_TYPE.TREND_TIME &&
                        isUnalignedMonthComparisonSpecialDate
                          ? 'The comparison period contains additional dates due to differences in month length. These extra dates have been excluded to maintain a one-to-one date alignment with the selected period.'
                          : 'Your comparison range includes a leap day. Totals displayed include the leap day values where applicable.'
                      }
                      arrow
                    >
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={
                          (item?.value === OTB_VALUE && !customBooking) ||
                          activeTab === REPORT_TYPE.TREND_HOTEL ||
                          (item?.value === OTB_VALUE && symmetricComparisonToggle && customBooking)
                        }
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        sx={{ pr: 1, mt: -2 }}
                      />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormGroup>
    </FormControl>
    {dateComparison && dateComparison?.includes(KEYS.CUSTOM) && (
      <>
        <FormControl>
          <ToggleButtonGroup
            fullWidth
            color="primary"
            value={customDate}
            exclusive
            onChange={handleCustomDates}
          >
            <ToggleButton key="start" value={KEYS.START_DATE}>
              Start Date
            </ToggleButton>
            <ToggleButton key="end" value={KEYS.END_DATE}>
              End Date
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
        <Grid container direction="row" alignItems="center" justifyContent="center" paddingTop={0}>
          <CustomDatePicker
            reportType={reportType}
            targetDate={targetDate}
            customDate={customDate}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
      </>
    )}
  </CustomStack>
);
//
export default TrendTimeVsComparisonWindow;
